<template>
    <FormulateForm v-model="formData" :errors="$page.props.errors" name="register" novalidate @submit="handleSubmit">
        <div class="form_field">
            <IconProfile class="form_field_prefix" />
            <FormulateInput
                type="text"
                name="name"
                validation="required"
                placeholder="First & Last Name"
            />
        </div>
        <div class="form_field">
            <IconEmail class="form_field_prefix" />
            <FormulateInput
                type="email"
                name="email"
                validation="required|email"
                placeholder="Email"
            />
        </div>
        <div class="form_field">
            <IconLock class="form_field_prefix" />
            <FormulateInput
                :type="!passwordVisibility.value ? 'password' : 'text'"
                name="password"
                placeholder="Password"
                validation="required|min:8,length|symbols|letters"
                :validation-rules="{
                    symbols: ({ value }) => /(?=.*[@$!%*#?&])/.test(value),
                    letters: ({ value }) => /(?=.*[A-Z])/.test(value),
                }"
                :validation-messages="{
                    required: 'Password is required.',
                    min: 'Must be at least 8 characters',
                    symbols: 'Symbols: !@#$%^&*',
                    letters: 'Uppercase letter: A-Z',
                }"
                :help="passwordStrength.value"
                @input="passwordStrength.check"
            >
                <template #help="{ help }">
                    <div
                        v-if="help"
                        :class="{
                            form_field_help: true,
                            form_field_help_weak: passwordStrength.value === 'weak',
                            form_field_help_medium: passwordStrength.value === 'medium',
                            form_field_help_strong: passwordStrength.value === 'strong',
                        }"
                    >
                        <IconPasswordShield />
                        <span>{{ help }}</span>
                    </div>
                </template>
            </FormulateInput>
            <button
                type="button"
                class="form_field_sufix"
                @click="passwordVisibility.toggle"
            >
                <IconEyeOpen v-if="passwordVisibility.value" />
                <IconEyeHide v-if="!passwordVisibility.value" />
            </button>
        </div>
        <div class="form_field">
            <IconLock class="form_field_prefix" />
            <FormulateInput
                :type="!passwordVisibility.value ? 'password' : 'text'"
                name="password_confirmation"
                placeholder="Confirm password"
                validation="required|matchPass"
                :validation-rules="{ matchPass: ({ value, getFormValues }) => value === getFormValues().password }"
                :validation-messages="{
                    required: 'Password is required.',
                    matchPass: 'Confirmation does not match.',
                }"
            />
            <button
                type="button"
                class="form_field_sufix"
                @click="passwordVisibility.toggle"
            >
                <IconEyeOpen v-if="passwordVisibility.value" />
                <IconEyeHide v-if="!passwordVisibility.value" />
            </button>
        </div>

        <div class="form_row_terms">
            <p>
                By connecting I confirm that I have read and accept the
                <Link class="loginWrap_buttonBack" href="/">
                    Terms & Conditions & Privacy Policy.
                </Link>
            </p>
        </div>
        <div data-classification="button" data-type="submit" class="submitButton formulate-input">
            <div class="formulate-input-wrapper">
                <div data-type="submit" class="formulate-input-element formulate-input-element--submit">
                    <vue-recaptcha
                        v-if="$page.props.environment === 'production'"
                        sitekey="6Le1LUolAAAAAOE36pmEQT2mtdNe-rB4uBFCoJMP"
                        size="invisible"
                        type="v2"
                    >
                        <button type="submit" @click="$formulate.submit('register')">
                            <span class="formulate-input-element--submit--label">sign up</span>
                        </button>
                    </vue-recaptcha>
                    <button v-else type="submit" @click="$formulate.submit('register')">
                        <span class="formulate-input-element--submit--label">sign up</span>
                    </button>
                </div>
            </div>
        </div>
    </FormulateForm>
</template>

<script setup lang="ts">
import IconEmail from '@/components/icons/IconEmail.vue';
import IconLock from '@/components/icons/IconLock.vue';
import IconEyeHide from '@/components/icons/IconEyeHide.vue';
import IconEyeOpen from '@/components/icons/IconEyeOpen.vue';
import IconPasswordShield from '@/components/icons/IconPasswordShield.vue';
import IconProfile from '@/components/icons/IconProfile.vue';
import type { Incomplete, RegisterFormData } from '../types';
import { usePasswordStrength, usePasswordVisibility } from '../composables/passwords';
import { VueRecaptcha } from 'vue-recaptcha';
import { reactive } from 'vue';
import { Link, router } from '@inertiajs/vue2';
import route from 'ziggy-js';

const formData = reactive<Incomplete<RegisterFormData>>({
    name: null,
    email: null,
    password: null,
    password_confirmation: null,
});

function handleSubmit(data: RegisterFormData) {
    router.post(route('register'), data);
}

const passwordVisibility = usePasswordVisibility();
const passwordStrength = usePasswordStrength();
</script>

<style lang="scss">
.form_row_terms {
  text-align: center;
  padding-top: 16px;
  p {
    font-family: "ITC Avant Garde Gothic Std",serif;
    max-width: 450px;
    margin: 0 auto;
    font-size: 14px;
    font-style: normal;
    line-height: 135%;
    color: #9d9dad;
    font-weight: 300;

    a {
      color: #0f0f0f;
      display: inline;
    }
  }
}

.form_row_recaptcha {
  padding-top: 48px;
  & > div {
    margin: 0 auto;
  }

  @media screen and (max-width: 1540px) {
    padding-top: 31px;
  }
}
</style>
